import { Fragment, useCallback, useRef, useState } from 'react';
import {
  Accordion,
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  FileButton,
  Group,
  List,
  Modal,
  Paper,
  Progress,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconArrowBackUp,
  IconChevronDown,
  IconChevronRight,
  IconDownload,
  IconFileSpreadsheet,
  IconInfoCircle,
  IconTrash,
} from '@tabler/icons-react';
import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@workos-inc/authkit-react';
import { Upload } from '~api';
import { joinPaths } from '~lib';
import { api } from '~portal/api';
import { clip, fillColumn, noShrink, noTopBorder } from '~portal/components/common/common.css';
import { UserEmail } from '~portal/components/UserEmail';
import { getAppEnv } from '~portal/env';
import { downloadFile } from '~portal/lib';

export const Route = createFileRoute('/_protected/admin/upload')({
  component: AdminUploadPage,
});

const fileTypeSelectData = Object.entries(Upload.fileTypeLabels).map(([value, label]) => ({
  value,
  label,
}));

type ConfirmIngest = {
  type: 'ingest';
  uploadId: string;
  filename: string;
  anomalyColumns: string[];
  anomalyRows: Array<Record<string, string>>;
};
type ConfirmRollback = { type: 'rollback'; uploadId: string; filename: string };
type ConfirmDelete = { type: 'delete'; uploadId: string; filename: string };
type PendingAction = ConfirmIngest | ConfirmRollback | ConfirmDelete;

type UploadState = {
  file: File | null;
  fileType: Upload.FileType | null;
  uploading: boolean;
  uploadProgress: number;
  error: string | null;
  expandedRows: Set<string>;
  pendingAction: PendingAction | null;
  helpDismissed: boolean;
};

function AdminUploadPage() {
  const { getAccessToken, user } = useAuth();
  const [confirmModalOpened, { open: openConfirmModal, close: closeConfirmModal }] =
    useDisclosure(false);
  const fileButtonResetRef = useRef<() => void>(null);
  const [state, setState] = useState<UploadState>({
    file: null,
    fileType: null,
    uploading: false,
    uploadProgress: 0,
    error: null,
    expandedRows: new Set(),
    pendingAction: null,
    helpDismissed: false,
  });

  // Fetch uploads list via tRPC
  const uploadsQuery = api.admin.listUploads.useQuery({}, { enabled: Boolean(user) });

  // tRPC utils for direct queries
  const apiUtils = api.useUtils();

  // Rollback mutation
  const rollbackMutation = api.admin.rollbackUpload.useMutation({
    onSuccess: () => {
      closeConfirmModal();
      setState((prev) => ({ ...prev, pendingAction: null, error: null }));
      uploadsQuery.refetch();
    },
    onError: (err) => {
      closeConfirmModal();
      setState((prev) => ({
        ...prev,
        pendingAction: null,
        error: err.message || 'Rollback failed',
      }));
      // CONFLICT means another admin's operation holds the lock — refresh to show current state
      if (err.data?.code === 'CONFLICT') {
        uploadsQuery.refetch();
      }
    },
  });

  // Delete mutation
  const deleteMutation = api.admin.deleteUpload.useMutation({
    onSuccess: () => {
      closeConfirmModal();
      setState((prev) => ({ ...prev, pendingAction: null, error: null }));
      uploadsQuery.refetch();
    },
    onError: (err) => {
      closeConfirmModal();
      setState((prev) => ({
        ...prev,
        pendingAction: null,
        error: err.message || 'Delete failed',
      }));
      // CONFLICT means another admin's operation holds the lock — refresh to show current state
      if (err.data?.code === 'CONFLICT') {
        uploadsQuery.refetch();
      }
    },
  });

  const env = getAppEnv();
  const uploadUrl = joinPaths(env.backendApiUrl, '/internal/admin/uploads');

  // Toggle row expansion
  const toggleRow = useCallback((uploadId: string) => {
    setState((prev) => {
      const newExpanded = new Set(prev.expandedRows);
      if (newExpanded.has(uploadId)) {
        newExpanded.delete(uploadId);
      } else {
        newExpanded.add(uploadId);
      }
      return { ...prev, expandedRows: newExpanded };
    });
  }, []);

  // File selection handler — auto-detect file type from name, fall back to manual dropdown
  const handleFileSelect = useCallback((file: File | null) => {
    if (!file) return;

    const detected = Upload.detectFileType(file.name);
    setState((prev) => ({
      ...prev,
      file,
      fileType: detected,
      uploading: false,
      uploadProgress: 0,
      error: null,
    }));
  }, []);

  // Upload handler
  const handleUpload = useCallback(async () => {
    if (!state.file || !state.fileType) return;

    setState((prev) => ({ ...prev, uploading: true, uploadProgress: 0, error: null }));

    try {
      const token = await getAccessToken();

      const xhr = new XMLHttpRequest();

      // Progress tracking
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const progress = (e.loaded / e.total) * 100;
          setState((prev) => ({ ...prev, uploadProgress: progress }));
        }
      });

      // Response handling
      await new Promise<Upload.Model<string, string>>((resolve, reject) => {
        xhr.addEventListener('load', () => {
          if (xhr.status === 201) {
            resolve(JSON.parse(xhr.responseText));
          } else {
            // Try to extract the error message from the JSON response body
            let message = `Upload failed: ${xhr.status} ${xhr.statusText}`;
            try {
              const body = JSON.parse(xhr.responseText);
              if (body.error && typeof body.error === 'string') {
                message = body.error;
              }
            } catch {
              // Response wasn't JSON — fall back to the status-based message
            }
            reject(new Error(message));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Network error during upload'));
        });

        xhr.open('POST', uploadUrl);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.setRequestHeader('Content-Type', 'application/octet-stream');
        xhr.setRequestHeader('X-Filename', state.file!.name);
        xhr.setRequestHeader('X-File-Type', state.fileType!);
        xhr.send(state.file);
      });

      setState((prev) => ({
        ...prev,
        uploading: false,
        uploadProgress: 100,
        file: null,
        fileType: null,
      }));

      // Reset the file input to allow re-selecting the same file
      fileButtonResetRef.current?.();

      // Refetch uploads list
      uploadsQuery.refetch();
    } catch (err) {
      const error = err instanceof Error ? err.message : 'Upload failed';
      setState((prev) => ({
        ...prev,
        uploading: false,
        uploadProgress: 0,
        error,
      }));
    }
  }, [state.file, state.fileType, getAccessToken, uploadUrl, uploadsQuery]);

  // Ingest handler with optimistic update
  const triggerIngest = api.admin.triggerIngest.useMutation({
    onMutate: async ({ uploadId }) => {
      setState((prev) => ({ ...prev, error: null }));

      // Cancel any outgoing refetches
      await apiUtils.admin.listUploads.cancel();

      // Snapshot the previous value
      const previousData = apiUtils.admin.listUploads.getData({});

      // Optimistically update to running status
      apiUtils.admin.listUploads.setData({}, (old) =>
        old?.map((upload) =>
          upload._id === uploadId
            ? {
                ...upload,
                ingestion: {
                  status: 'running' as const,
                  startedAt: new Date(),
                  finishedAt: null,
                },
              }
            : upload,
        ),
      );

      return { previousData };
    },
    onError: (err, _variables, context) => {
      // Rollback to previous data on error
      if (context?.previousData) {
        apiUtils.admin.listUploads.setData({}, context.previousData);
      }
      const error = err.message || 'Ingestion failed';
      setState((prev) => ({ ...prev, error }));
    },
    onSettled: () => {
      // Refetch to ensure server state
      uploadsQuery.refetch();
    },
  });

  // Ingest handler that shows confirmation dialog if anomalies present
  const handleIngestWithConfirm = (uploadId: string, upload: Upload.Serial) => {
    const anomalyCount = upload.validation?.summary?.anomalyCount ?? 0;
    if (!anomalyCount) return triggerIngest.mutate({ uploadId });

    const rows: Array<Record<string, string>> = [];
    const allKeys = new Set<string>();

    upload.validation?.sheets?.forEach((sheet) => {
      for (const anomaly of sheet.anomalies ?? []) {
        for (const key of Object.keys(anomaly)) {
          allKeys.add(key);
        }
        rows.push(anomaly);
      }
    });

    setState((prev) => ({
      ...prev,
      pendingAction: {
        type: 'ingest',
        uploadId,
        filename: upload.filename,
        anomalyColumns: [...allKeys],
        anomalyRows: rows,
      },
    }));
    openConfirmModal();
  };

  // Handler to open confirmation modal
  const handleConfirmAction = (type: 'rollback' | 'delete', uploadId: string, filename: string) => {
    setState((prev) => ({ ...prev, pendingAction: { type, uploadId, filename } }));
    openConfirmModal();
  };

  // Handler to execute the confirmed action
  const executeConfirmedAction = () => {
    if (!state.pendingAction) return;

    const { type, uploadId } = state.pendingAction;
    switch (type) {
      case 'ingest':
        closeConfirmModal();
        setState((prev) => ({ ...prev, pendingAction: null }));
        triggerIngest.mutate({ uploadId });
        break;
      case 'rollback':
        rollbackMutation.mutate({ uploadId });
        break;
      default:
        deleteMutation.mutate({ uploadId });
        break;
    }
  };

  // Handler for download
  const handleDownload = async (uploadId: string, filename: string) => {
    try {
      const { url } = await apiUtils.admin.getUploadDownloadUrl.fetch({ uploadId });
      downloadFile(url, filename);
    } catch (err) {
      const error = err instanceof Error ? err.message : 'Download failed';
      setState((prev) => ({ ...prev, error }));
    }
  };

  return (
    <Stack gap="lg" p="lg" h="100%" className={clip}>
      {/* Confirmation Modal */}
      <Modal
        opened={confirmModalOpened}
        onClose={() => {
          closeConfirmModal();
          setState((prev) => ({ ...prev, pendingAction: null }));
        }}
        title={(() => {
          switch (state.pendingAction?.type) {
            case 'rollback':
              return 'Confirm Rollback';
            case 'ingest':
              return 'Confirm Ingestion';
            default:
              return 'Confirm Delete';
          }
        })()}
        size="2xl"
        centered
      >
        <Stack gap="md">
          {(() => {
            // Bind to a const so the 'ingest' narrowing survives the nested map closures.
            const action = state.pendingAction;
            if (action?.type === 'ingest') {
              return (
                <>
                  <Alert variant="light" color="yellow" title="Unfamiliar entries detected">
                    This file contains{' '}
                    <Text span fw={600}>
                      {action.anomalyRows.length}
                    </Text>{' '}
                    categor{action.anomalyRows.length === 1 ? 'y' : 'ies'} with dimensions the
                    system hasn't seen before. Review them below — they will be added to the
                    database if you proceed.
                  </Alert>
                  <Box>
                    <Text size="sm" fw={600} mb="xs">
                      New entries in{' '}
                      <Text span fw={700} component="span">
                        {action.filename}
                      </Text>
                      :
                    </Text>
                    <ScrollArea.Autosize mah={300}>
                      <Table withTableBorder withColumnBorders striped>
                        <Table.Thead>
                          <Table.Tr>
                            {action.anomalyColumns.map((col) => (
                              <Table.Th key={col}>{col}</Table.Th>
                            ))}
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {action.anomalyRows.map((row, i) => (
                            <Table.Tr key={i}>
                              {action.anomalyColumns.map((col) => (
                                <Table.Td key={col}>{row[col] ?? '-'}</Table.Td>
                              ))}
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </ScrollArea.Autosize>
                  </Box>
                </>
              );
            }
            return (
              <Text>
                {action?.type === 'rollback' ? (
                  <>
                    Are you sure you want to rollback the ingestion for{' '}
                    <Text span fw={600}>
                      {action?.filename}
                    </Text>
                    ? This will remove all data from ClickHouse for this upload.
                  </>
                ) : (
                  <>
                    Are you sure you want to delete{' '}
                    <Text span fw={600}>
                      {action?.filename}
                    </Text>
                    ? This will permanently remove the file from storage.
                  </>
                )}
              </Text>
            );
          })()}
          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => {
                closeConfirmModal();
                setState((prev) => ({ ...prev, pendingAction: null }));
              }}
            >
              Cancel
            </Button>
            <Button
              color={state.pendingAction?.type === 'ingest' ? 'green' : 'red'}
              onClick={executeConfirmedAction}
              loading={
                triggerIngest.isPending || rollbackMutation.isPending || deleteMutation.isPending
              }
            >
              {state.pendingAction?.type === 'rollback'
                ? 'Rollback'
                : state.pendingAction?.type === 'ingest'
                  ? 'Ingest'
                  : 'Delete'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Title order={1}>Upload Data</Title>

      {!state.helpDismissed && (
        <Alert
          variant="light"
          color="blue"
          icon={<IconInfoCircle size={20} />}
          title="How it works"
          withCloseButton
          onClose={() => setState((prev) => ({ ...prev, helpDismissed: true }))}
          className={noShrink}
        >
          <Stack gap="xs">
            <Text size="sm">
              Upload XLSX data files for validation and ingestion into the analytics database. The
              system auto-detects the file type from the filename — if detection fails, you will be
              prompted to select it manually.
            </Text>
            <Text size="sm" fw={600}>
              Supported file types and naming patterns:
            </Text>
            <List size="sm" spacing={2}>
              <List.Item>
                <Text span fw={500}>
                  GGR Annual / Monthly
                </Text>{' '}
                — filename must contain &quot;GGR Annual&quot; or &quot;GGR Monthly&quot;
              </List.Item>
              <List.Item>
                <Text span fw={500}>
                  Turnover Annual
                </Text>{' '}
                — &quot;Turnover Annual&quot;
              </List.Item>
              <List.Item>
                <Text span fw={500}>
                  Market Share
                </Text>{' '}
                — &quot;Monthly Market Share&quot;, &quot;Market Share Monthly&quot;, &quot;Annual
                Market Share&quot;, or &quot;Market Share Annual&quot;
              </List.Item>
              <List.Item>
                <Text span fw={500}>
                  FX
                </Text>{' '}
                — &quot;FX&quot; as a standalone word (not part of another word)
              </List.Item>
              <List.Item>
                <Text span fw={500}>
                  Country Statistics
                </Text>{' '}
                — &quot;KPIs&quot;
              </List.Item>
              <List.Item>
                <Text span fw={500}>
                  Web Analytics
                </Text>{' '}
                — &quot;Web Analytics&quot;
              </List.Item>
            </List>
            <Text size="sm" c="dimmed">
              After upload, XLSX files are automatically validated. Files with a &quot;valid&quot;
              status can then be ingested into ClickHouse. Ingestion can be rolled back if needed.
            </Text>
          </Stack>
        </Alert>
      )}

      {/* Upload Form */}
      <Paper withBorder p="md" className={noShrink}>
        <Stack gap="md">
          <Group gap="md" wrap="wrap" align="center">
            <FileButton onChange={handleFileSelect} accept=".xlsx" resetRef={fileButtonResetRef}>
              {(props) => (
                <Button {...props} variant="light" leftSection={<IconFileSpreadsheet size={16} />}>
                  {state.file ? 'Change File' : 'Select XLSX File'}
                </Button>
              )}
            </FileButton>

            {state.file && (
              <>
                <Group gap="xs">
                  <Text size="sm" fw={500}>
                    {state.file.name}
                  </Text>
                  <Text size="xs" c="dimmed">
                    (
                    {state.file.size >= 1024 * 1000
                      ? `${(state.file.size / 1024 / 1024).toFixed(1)} MB`
                      : `${(state.file.size / 1024).toFixed(1)} KB`}
                    )
                  </Text>
                </Group>

                {/* Show detected type so the user knows what will be uploaded */}
                {state.fileType && Upload.detectFileType(state.file.name) && (
                  <Badge variant="light" color="teal" size="lg">
                    {Upload.fileTypeLabels[state.fileType]}
                  </Badge>
                )}

                <Button
                  onClick={handleUpload}
                  disabled={!state.fileType || state.uploading}
                  loading={state.uploading}
                >
                  Upload
                </Button>
              </>
            )}
          </Group>

          {state.uploading && <Progress value={state.uploadProgress} animated size="sm" />}

          {/* Manual file type selection — shown only when auto-detection failed */}
          {state.file && !Upload.detectFileType(state.file.name) && (
            <Alert color="yellow" title="Unrecognised filename" variant="light">
              <Stack gap="sm">
                <Text size="sm">
                  Could not detect the file type from the filename. Please select it manually.
                </Text>
                <Select
                  placeholder="Select file type"
                  data={fileTypeSelectData}
                  value={state.fileType}
                  onChange={(value) =>
                    setState((prev) => ({ ...prev, fileType: value as Upload.FileType | null }))
                  }
                  w={250}
                  aria-label="File type"
                />
              </Stack>
            </Alert>
          )}
        </Stack>
      </Paper>

      {state.error && (
        <Alert
          color="red"
          title="Error"
          withCloseButton
          onClose={() => setState((prev) => ({ ...prev, error: null }))}
        >
          {state.error}
        </Alert>
      )}

      {/* Upload List */}
      <Paper withBorder p="md" className={fillColumn}>
        <Group justify="space-between" mb="md">
          <Title order={3}>Recent Uploads</Title>
          <Button
            onClick={() => uploadsQuery.refetch()}
            loading={uploadsQuery.isLoading}
            size="xs"
            variant="light"
          >
            Refresh
          </Button>
        </Group>

        {!uploadsQuery.data || uploadsQuery.data.length === 0 ? (
          <Text c="dimmed" size="sm">
            No uploads yet
          </Text>
        ) : (
          <ScrollArea flex={1}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={40} />
                  <Table.Th>Filename</Table.Th>
                  <Table.Th>Uploaded By</Table.Th>
                  <Table.Th>Validation</Table.Th>
                  <Table.Th>Ingestion</Table.Th>
                  <Table.Th>Uploaded</Table.Th>
                  <Table.Th>Sheets</Table.Th>
                  <Table.Th>Errors</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {uploadsQuery.data?.map((upload) => {
                  const isExpanded = state.expandedRows.has(upload._id);
                  const hasValidation =
                    (upload.validation?.sheets && upload.validation.sheets.length > 0) ||
                    !!upload.validation?.error;

                  return (
                    <Fragment key={upload._id}>
                      <Table.Tr
                        onClick={() => hasValidation && toggleRow(upload._id)}
                        onKeyDown={(e) => {
                          if (hasValidation && (e.key === 'Enter' || e.key === ' ')) {
                            e.preventDefault();
                            toggleRow(upload._id);
                          }
                        }}
                        tabIndex={hasValidation ? 0 : undefined}
                        role={hasValidation ? 'button' : undefined}
                        aria-expanded={hasValidation ? isExpanded : undefined}
                        style={{ cursor: hasValidation ? 'pointer' : undefined }}
                      >
                        <Table.Td>
                          {hasValidation && (
                            <Box p={4}>
                              {isExpanded ? (
                                <IconChevronDown size={16} />
                              ) : (
                                <IconChevronRight size={16} />
                              )}
                            </Box>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" lineClamp={1}>
                            {upload.filename}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <UserEmail userId={upload.uploadedBy} />
                        </Table.Td>
                        <Table.Td>
                          <ValidationStatusBadge status={upload.validation?.status} />
                        </Table.Td>
                        <Table.Td>
                          <IngestionStatusBadge ingestion={upload.ingestion} />
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{new Date(upload.createdAt).toLocaleDateString()}</Text>
                        </Table.Td>
                        <Table.Td>{upload.validation?.summary?.totalSheets || 0}</Table.Td>
                        <Table.Td>
                          {(() => {
                            const summary = upload.validation?.summary;
                            // errorCount excludes anomalies (warnings), shown as a separate badge.
                            const errorCount = summary?.errorCount ?? 0;
                            return (
                              <Group gap={4}>
                                {errorCount > 0 ? (
                                  <Badge color="red" variant="light" size="sm">
                                    {errorCount}
                                  </Badge>
                                ) : (
                                  <Text size="sm" c="dimmed">
                                    0
                                  </Text>
                                )}
                                {(summary?.anomalyCount ?? 0) > 0 && (
                                  <Badge color="yellow" variant="light" size="sm" ml={4}>
                                    {summary?.anomalyCount} anomal
                                    {summary?.anomalyCount === 1 ? 'y' : 'ies'}
                                  </Badge>
                                )}
                              </Group>
                            );
                          })()}
                        </Table.Td>
                        <Table.Td>
                          <Group gap={4} wrap="nowrap" onClick={(e) => e.stopPropagation()}>
                            <Tooltip label="Download" position="top" withArrow>
                              <ActionIcon
                                variant="subtle"
                                onClick={() => handleDownload(upload._id, upload.filename)}
                                aria-label={`Download ${upload.filename}`}
                              >
                                <IconDownload size={18} />
                              </ActionIcon>
                            </Tooltip>

                            {upload.ingestion?.status !== 'done' &&
                              (upload.ingestion?.status !== 'running' ||
                                Upload.isIngestionStale(upload.ingestion)) && (
                                <Tooltip label="Delete" position="top" withArrow>
                                  <ActionIcon
                                    variant="light"
                                    color="red"
                                    onClick={() =>
                                      handleConfirmAction('delete', upload._id, upload.filename)
                                    }
                                    aria-label={`Delete ${upload.filename}`}
                                  >
                                    <IconTrash size={18} />
                                  </ActionIcon>
                                </Tooltip>
                              )}

                            {upload.validation?.status === 'valid' &&
                              upload.ingestion?.status !== 'done' &&
                              (() => {
                                // Scoped to this row. Disabling every Ingest button while
                                // *any* upload was running meant one stalled file blocked
                                // the whole table — ingests of different uploads never
                                // contended in the first place, the distributed lock is
                                // per-upload (H2-226).
                                const stalled = Upload.isIngestionStale(upload.ingestion);
                                const running = upload.ingestion?.status === 'running';
                                return (
                                  <Button
                                    size="compact-sm"
                                    onClick={() => handleIngestWithConfirm(upload._id, upload)}
                                    loading={running && !stalled}
                                    disabled={running && !stalled}
                                    color={stalled ? 'red' : 'green'}
                                  >
                                    {stalled
                                      ? 'Retry'
                                      : running
                                        ? 'Ingesting...'
                                        : upload.ingestion?.status === 'failed'
                                          ? 'Retry'
                                          : 'Ingest'}
                                  </Button>
                                );
                              })()}

                            {upload.ingestion?.status === 'done' && (
                              <Tooltip label="Rollback" position="top" withArrow>
                                <ActionIcon
                                  variant="light"
                                  color="orange"
                                  onClick={() =>
                                    handleConfirmAction('rollback', upload._id, upload.filename)
                                  }
                                  aria-label={`Rollback ingestion for ${upload.filename}`}
                                >
                                  <IconArrowBackUp size={18} />
                                </ActionIcon>
                              </Tooltip>
                            )}
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                      {hasValidation && isExpanded && (
                        <Table.Tr>
                          <Table.Td colSpan={9} p={0} className={noTopBorder}>
                            <ValidationDetails validation={upload.validation!} />
                          </Table.Td>
                        </Table.Tr>
                      )}
                    </Fragment>
                  );
                })}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}
      </Paper>
    </Stack>
  );
}

function ValidationStatusBadge({ status }: { status?: Upload.ValidationStatus }) {
  if (!status) {
    return (
      <Badge color="gray" variant="filled" size="sm">
        pending
      </Badge>
    );
  }

  const colorMap: Record<Upload.ValidationStatus, string> = {
    valid: 'green',
    invalid: 'red',
    failed: 'red',
  };

  return (
    <Badge color={colorMap[status]} variant="filled" size="sm">
      {status}
    </Badge>
  );
}

function IngestionStatusBadge({ ingestion }: { ingestion?: Upload.Serial['ingestion'] }) {
  if (!ingestion) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }

  // A run that stopped reporting is shown as stalled rather than as still running:
  // the row is dead, and labelling it "running" is what hid the September incident
  // behind a spinner (H2-226).
  if (Upload.isIngestionStale(ingestion)) {
    return (
      <Badge color="red" variant="filled" size="sm">
        stalled
      </Badge>
    );
  }

  const colorMap: Record<Upload.IngestionStatus, string> = {
    running: 'blue',
    done: 'teal',
    failed: 'orange',
  };

  return (
    <Badge color={colorMap[ingestion.status]} variant="filled" size="sm">
      {ingestion.status}
    </Badge>
  );
}

function ValidationDetails({ validation }: { validation: Upload.Validation }) {
  const hasSheets = validation.sheets && validation.sheets.length > 0;
  if (!hasSheets && !validation.error) {
    return null;
  }

  return (
    <Box bg="dark.8" p="md">
      {validation.error && (
        <Alert color="red" title="Validation Error" mb="md">
          {validation.error}
        </Alert>
      )}

      {validation.summary && (
        <Group gap="lg" mb="md">
          <Text size="sm">
            <Text span fw={600}>
              Total Rows:
            </Text>{' '}
            {validation.summary.totalRows}
          </Text>
          <Text size="sm">
            <Text span fw={600}>
              Valid:
            </Text>{' '}
            {validation.summary.validRows}
          </Text>
          <Text size="sm" c={validation.summary.errorCount > 0 ? 'red' : undefined}>
            <Text span fw={600}>
              Errors:
            </Text>{' '}
            {validation.summary.errorCount}
          </Text>
          {(validation.summary.anomalyCount ?? 0) > 0 && (
            <Text size="sm" c="yellow">
              <Text span fw={600}>
                Anomalies:
              </Text>{' '}
              {validation.summary.anomalyCount}
            </Text>
          )}
        </Group>
      )}

      {hasSheets && (
        <Accordion variant="separated" multiple>
          {validation.sheets?.map((sheet) => (
            <SheetAccordionItem key={sheet.name} sheet={sheet} />
          ))}
        </Accordion>
      )}
    </Box>
  );
}

/** Convert camelCase field name to human-readable label, e.g. "subChannel" → "Sub Channel" */
function formatFieldLabel(name: string): string {
  return name
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
}

/** Render identifying dimension context as a compact comma-separated tuple */
function RecordContext({ context, row }: { context?: Record<string, string>; row: number }) {
  if (!context || Object.keys(context).length === 0) {
    return <Text size="xs">Row {row}</Text>;
  }

  return (
    <Text size="xs" lineClamp={3}>
      {Object.entries(context).map(([key, value], i) => (
        <Fragment key={key}>
          {i > 0 && ', '}
          <Text span size="xs" fw={600}>
            {formatFieldLabel(key)}
          </Text>{' '}
          {value}
        </Fragment>
      ))}
    </Text>
  );
}

function SheetAccordionItem({ sheet }: { sheet: Upload.SheetValidation }) {
  const anomalies = sheet.anomalies ?? [];
  const hasIssues = sheet.errors.length > 0 || anomalies.length > 0;

  if (!hasIssues) {
    return (
      <Accordion.Item value={sheet.name}>
        <Accordion.Control>
          <Group gap="sm">
            <Text fw={500}>{sheet.name}</Text>
            <Badge color="gray" variant="light" size="xs">
              {sheet.metricType}
            </Badge>
            <Badge color="green" variant="light" size="xs">
              {sheet.validRows}/{sheet.totalRows} valid
            </Badge>
          </Group>
        </Accordion.Control>
        <Accordion.Panel>
          <Text size="sm" c="dimmed">
            No issues found in this sheet.
          </Text>
        </Accordion.Panel>
      </Accordion.Item>
    );
  }

  return (
    <Accordion.Item value={sheet.name}>
      <Accordion.Control>
        <Group gap="sm">
          <Text fw={500}>{sheet.name}</Text>
          <Badge color="gray" variant="light" size="xs">
            {sheet.metricType}
          </Badge>
          <Badge color="green" variant="light" size="xs">
            {sheet.validRows}/{sheet.totalRows} valid
          </Badge>
          {sheet.errors.length > 0 && (
            <Badge color="red" variant="filled" size="xs">
              {sheet.errors.length} errors
            </Badge>
          )}
          {anomalies.length > 0 && (
            <Badge color="yellow" variant="filled" size="xs">
              {anomalies.length} anomal{anomalies.length === 1 ? 'y' : 'ies'}
            </Badge>
          )}
        </Group>
      </Accordion.Control>
      <Accordion.Panel>
        {sheet.errors.length > 0 && (
          <ScrollArea.Autosize mah={200}>
            <Table withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Record</Table.Th>
                  <Table.Th w={120}>Field</Table.Th>
                  <Table.Th>Message</Table.Th>
                  <Table.Th w={100}>Value</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {sheet.errors.map((error, idx) => (
                  <Table.Tr key={idx}>
                    <Table.Td>
                      <RecordContext context={error.context} row={error.row} />
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {error.field}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">{error.message}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed" lineClamp={1}>
                        {error.value !== undefined ? String(error.value) : '-'}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea.Autosize>
        )}
        {anomalies.length > 0 && (
          <ScrollArea.Autosize mah={200}>
            <Text size="xs" fw={600} c="yellow" mb="xs">
              Anomalies
            </Text>
            <Table withTableBorder>
              <Table.Tbody>
                {anomalies.map((tuple, idx) => (
                  <Table.Tr key={`warn-${idx}`}>
                    <Table.Td>
                      <Text size="xs" c="yellow">
                        {Object.entries(tuple)
                          .map(([k, v]) => `${k}=${v}`)
                          .join(', ')}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea.Autosize>
        )}
      </Accordion.Panel>
    </Accordion.Item>
  );
}
